import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DriftformDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/driftform-dto';
import { FabrikatDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/fabrikat-dto';
import { forkJoin, ReplaySubject, Subject } from 'rxjs';
import { FabrikatService, InstallatoerService, ServiceAftaleService } from 'src/app/api/services';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { distinctUntilChanged, mergeMap, takeUntil } from 'rxjs/operators';
import { AnlaegDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/anlaeg-dto';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EffektService } from 'src/app/services/aftale-helper.service';
import { effektValidator } from 'src/app/validators/effekt.validator';
import { ProduktDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/produkt-dto';
import { AnlaegEditDialogData, DialogType } from 'src/app/components/aftale/models';
import moment from 'moment';
import { ConfirmDialogComponent } from 'src/app/components/shared/confirm-dialog/confirm-dialog.component';
import { EnvironmentLoaderService } from 'src/app/services/environment-loader.service';
import { ProduktEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/produkt-enum';
import { InstallatoerDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/installatoer-dto';
import { StamdataStatusEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/stamdata-status-enum';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ModelService } from 'src/app/api/services/model.service';
import { AuthService } from 'src/app/services/auth.service';
import { ModelDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/model-dto';
import { fabrikatValidator } from 'src/app/validators/fabrikat.validator';
import { modelValidator } from 'src/app/validators/model.validator';

@Component({
    selector: 'app-anlaeg-edit',
    templateUrl: './anlaeg-edit.component.html',
    styleUrls: ['./anlaeg-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AnlaegEditComponent implements OnInit {
  anlaeg: AnlaegDto;
  product: ProduktDto;

  anlaegForm: UntypedFormGroup;
  driftformer: DriftformDto[];
  fabrikater: FabrikatDto[] = [];
  modeller: ModelDto[] = [];
  installatoerer: InstallatoerDto[];
  unsubscribe$ = new Subject<void>();
  isStorBraender: boolean;
  fabrikatWarning: boolean = false;
  modelWarning: boolean = false;

  canWrite: boolean = false;

  private readonly MIN_POWER_FOR_STORBRAENDER: number = 135;

  selectedFabrikat$: ReplaySubject<string> = new ReplaySubject();
  selectedModel$: ReplaySubject<string> = new ReplaySubject();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private fabrikatService: FabrikatService,
    private errorHandler: ErrorHandlerService,
    private serviceAftaleService: ServiceAftaleService,
    private effektService: EffektService,
    private dialog: MatDialogRef<AnlaegEditComponent>,
    private confirmDialog: MatDialog,
    private env: EnvironmentLoaderService,
    private installatoerService: InstallatoerService,
    private notifications: NotificationService,
    private modelService: ModelService,
    private auth: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: AnlaegEditDialogData
  ) {
    this.canWrite = this.auth.canWrite;
  }

  hasOlderKomponenter: boolean;

  get fabrikatId() {
    return this.anlaegForm.get('fabrikatId');
  }

  get alternativFabrikat() {
    return this.anlaegForm.get('alternativFabrikat');
  }

  get modelId() {
    return this.anlaegForm.get('modelId');
  }

  get alternativModel() {
    return this.anlaegForm.get('alternativModel');
  }

  get storbraender() {
    return this.anlaegForm.get('storbraender');
  }

  get effekt() {
    return this.anlaegForm.get('effekt');
  }

  get installatoerId() {
    return this.anlaegForm.get('installatoerId');
  }

  get alternativInstallatoer() {
    return this.anlaegForm.get('alternativInstallatoer');
  }

  get installationDate() {
    return this.anlaegForm.get('installationDate');
  }

  get serienummer() {
    return this.anlaegForm.get('serienummer');
  }

  get garantiPeriode() {
    return this.anlaegForm.get('garantiPeriode');
  }

  get driftformId() {
    return this.anlaegForm.get('driftformId');
  }

  get kondenserende() {
    return this.anlaegForm.get('kondenserende');
  }

  ngOnInit(): void {
    this.product = this.data.product;
    this.anlaeg = this.data.anlaeg;

    const effektStr = this.effektService.setEffekt(this.anlaeg.minEffekt, this.anlaeg.maxEffekt);
    this.anlaegForm = this.formBuilder.group(
      {
        alternativFabrikat: [this.anlaeg.alternativFabrikat],
        alternativModel: [this.anlaeg.alternativModel],
        model: [this.anlaeg.model],
        installationDate: [this.anlaeg.installationDate ? new Date(this.anlaeg.installationDate) : null],
        serienummer: [this.anlaeg.serienummer],
        garantiPeriode: [this.anlaeg.garantiPeriode],
        effekt: [effektStr, [Validators.pattern(this.effektService.effektPattern), effektValidator]],
        driftformId: [this.anlaeg.driftform?.id],
        fabrikatId: [this.anlaeg.fabrikat?.id],
        modelId: [this.anlaeg.model?.id],
        kondenserende: [this.anlaeg.kondenserende],
        storbraender: [this.anlaeg.storbraender],
        produktId: [this.product?.id],
        unknownFabrikat:
          this.anlaeg.fabrikat == null && (this.anlaeg.alternativFabrikat == null || this.anlaeg.alternativFabrikat.trim() === ''),
        unknownModel: this.anlaeg.model == null && (this.anlaeg.alternativModel == null || this.anlaeg.alternativModel.trim() === ''),
        installatoerId: [this.anlaeg.installatoer?.id],
        alternativInstallatoer: [this.anlaeg.alternativInstallatoer],
      },
      { validators: [fabrikatValidator, modelValidator] }
    );

    if (!this.installationDate.value) {
      this.garantiPeriode.setValue(null);
      this.garantiPeriode.disable();
    }

    this.checkOlderKomponenter();

    this.isStorBraender = this.anlaeg.storbraender;

    this.effekt.valueChanges.pipe(takeUntil(this.unsubscribe$), distinctUntilChanged()).subscribe({
      next: (value) => {
        //TODO we need a more robust way to test this
        if (this.product.text != 'Varmepumpe' && value >= this.MIN_POWER_FOR_STORBRAENDER && this.anlaegForm.dirty) {
          this.storbraender.patchValue(true);
          this.notifications.success('Storbrænder er automatisk slået til da effekten overstiger 135 kW');
        }
      },
    });

    this.installationDate.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      if (!val) {
        this.garantiPeriode.disable();
        this.garantiPeriode.setValue(null);
      } else {
        this.garantiPeriode.enable();
      }
    });

    this.storbraender.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      this.isStorBraender = val;
      //Olie and gas
      if ((val && this.product.id == ProduktEnum.Olie) || this.product.id == ProduktEnum.Gas) {
        this.driftformId.setValidators(Validators.required);
      } else {
        this.driftformId.clearValidators();
      }
      this.driftformId.updateValueAndValidity();
    });

    this.fabrikatId.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        mergeMap((fabrikatId: number) => {
          return this.modelService.getModellerForFabrikat({ fabrikatId: fabrikatId, caller: EGIS_SERVICE_CALLER });
        })
      )
      .subscribe((modeller: ModelDto[]) => {
        this.modeller = modeller;
      });
    this.selectedFabrikat$.next(this.anlaeg.fabrikat?.name ?? this.anlaeg.alternativFabrikat);
    this.selectedModel$.next(this.anlaeg.model?.name ?? this.anlaeg.alternativModel);

    this.fabrikatService.getFabrikaterForProdukt({ produktId: this.product.id, caller: EGIS_SERVICE_CALLER }).subscribe(
      (data) => {
        this.fabrikater = data;
      },
      (error: HttpErrorResponse) => {
        this.errorHandler.HandleError(error);
      }
    );

    forkJoin([
      this.fabrikatService.getFabrikaterForProdukt({ produktId: this.data.product.id, caller: EGIS_SERVICE_CALLER }),
      this.serviceAftaleService.getAvailableDriftformer({ caller: EGIS_SERVICE_CALLER }),
      this.installatoerService.getInstallatoerer({
        body: { searchQuery: '' },
        caller: EGIS_SERVICE_CALLER,
      }),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (results: any[]) => {
          this.fabrikater = results[0];
          this.driftformer = results[1];
          this.installatoerer = results[2];
        },
        error: (error) => {
          this.errorHandler.HandleError(error);
        },
      });

    if (this.anlaeg.fabrikat) {
      this.modelService
        .getModellerForFabrikat({
          fabrikatId: this.anlaeg.fabrikat?.id,
          caller: EGIS_SERVICE_CALLER,
        })
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (modeller: ModelDto[]) => {
            this.modeller = modeller;
          },
          error: (error) => {
            this.errorHandler.HandleError(error);
          },
        });
    }

    this.installationDate.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      if (!val) {
        this.garantiPeriode.disable();
        this.garantiPeriode.setValue(null);
      } else {
        this.garantiPeriode.enable();
      }
      this.checkOlderKomponenter();
    });
  }

  checkOlderKomponenter() {
    this.hasOlderKomponenter = false;
    if (this.installationDate.value) {
      this.anlaeg.komponenter.forEach((komponent) => {
        if (moment(komponent.installationDate).isBefore(moment(this.installationDate.value))) {
          this.hasOlderKomponenter = true;
        }
      });
    }
  }

  onFabrikatChange(fabrikatDto: FabrikatDto) {
    this.fabrikatWarning = false;
    if (fabrikatDto) {
      this.fabrikatId.setValidators(Validators.required);
      this.fabrikatId.setValue(fabrikatDto.id);
      if (fabrikatDto.fabrikatstatus == StamdataStatusEnum.Maybe) this.fabrikatWarning = true;
    } else {
      this.fabrikatId.setValue(null);
      this.fabrikatId.clearValidators();
      this.fabrikatId.reset();
      this.modelId.setValue(null);
      this.modelId.clearValidators();
      this.modelId.reset();
    }
  }

  onModelChange(model: string) {
    this.modelWarning = false;
    let dto = this.modeller.find((m) => m.name == model);
    if (dto) {
      this.modelId.setValue(dto.id);
      this.alternativModel.reset();
    } else {
      this.alternativModel.setValue(model);
      this.modelId.reset();
    }
  }

  onInstallatoerChange(installatoer: string) {
    let dto = this.installatoerer.find((i) => i.name == installatoer);
    if (dto) {
      this.installatoerId.setValue(dto.id);
      this.alternativInstallatoer.reset();
    } else {
      this.alternativInstallatoer.setValue(installatoer);
      this.installatoerId.reset();
    }
  }

  save() {
    this.hasOlderKomponenter = false;
    this.anlaeg.komponenter.forEach((komponent) => {
      if (moment(komponent.installationDate).isBefore(moment(this.installationDate.value))) {
        komponent.installationDate = this.installationDate.value.toOkDateString();
      }
    });

    let effekt = this.effektService.getEffekt(this.effekt.value);
    let fabrikat = this.fabrikater.find((x) => x.id == this.fabrikatId.value);
    let model = this.modeller.find((x) => x.id == this.modelId.value);
    let driftform = this.driftformer.find((x) => x.id == this.driftformId.value);
    let anlaeg: AnlaegDto = {
      komponenter: this.anlaeg.komponenter,
      fabrikat: fabrikat,
      alternativFabrikat: this.alternativFabrikat.value,
      alternativModel: this.alternativModel.value,
      driftform: this.isStorBraender ? driftform : null,
      installationDate: this.installationDate.value ? this.installationDate.value.toOkDateString() : null,
      model: model,
      minEffekt: effekt.minEffekt ? Number(effekt.minEffekt) : null,
      maxEffekt: effekt.maxEffekt ? Number(effekt.maxEffekt) : null,
      kondenserende: this.kondenserende.value,
      storbraender: this.storbraender.value,
      id: this.anlaeg.id,
      garantiPeriode: this.garantiPeriode.value,
      serienummer: this.serienummer.value,
      installatoer: this.installatoerer.find((x) => x.id == this.installatoerId.value),
      alternativInstallatoer: this.alternativInstallatoer.value,
    };
    this.dialog.close({ anlaeg: anlaeg });
  }

  cancel() {
    this.dialog.close(null);
  }
}
